import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { Alert, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { NoData } from "components/dataDisplay/NoData";
import { TextDisplay } from "components/dataDisplay/TextDisplay";
import type { FC } from "react";
import type { TFunction } from "react-i18next";
import ColorEffectsDisplay from "../dataDisplay/ColorEffectsDisplay";
import ColorTagsDisplay from "../dataDisplay/ColorTagsDisplay";
import { SearchForThisColorCodeButton } from "./SearchForThisColorCodeButton";

export interface ColorAndMaterialInformationProps {
  material: ColoredMaterial;
  isInternal?: boolean;
  t: TFunction;
}

export const ColorAndMaterialInformation: FC<
  ColorAndMaterialInformationProps
> = ({ material, isInternal, t }) => {
  return (
    <Grid container spacing={1}>
      <Grid size={5}>
        <Typography variant="label">{t("material_branch")}</Typography>
      </Grid>
      <Grid size={7}>
        <TextDisplay
          value={material.material.brand}
          fallback={<NoData label="n/a" />}
        />
      </Grid>
      {isInternal && (
        <>
          <Grid size={5}>
            <Typography variant="label">{t("material_grade")}</Typography>
          </Grid>
          <Grid size={7}>
            <TextDisplay
              value={material.material.shortGrade}
              fallback={<NoData label="n/a" />}
            />
          </Grid>
        </>
      )}
      <Grid size={5}>
        <Typography variant="label">{t("final_color_code")}</Typography>
      </Grid>
      <Grid size={7}>
        <TextDisplay value={material.color.finalColorCode} />
        <SearchForThisColorCodeButton
          finalColorCode={material.color.finalColorCode}
        />
      </Grid>
      <Grid size={5}>
        <Typography variant="label">{t("preliminary_color_code")}</Typography>
      </Grid>
      <Grid size={7}>
        <TextDisplay value={material.color.preliminaryColorCode} />
      </Grid>
      {isInternal && (
        <>
          <Grid size={5}>
            <Typography variant="label">{t("color_system")}</Typography>
          </Grid>
          <Grid size={7}>
            <TextDisplay value={material.color.colorSystem} />
          </Grid>
          <Grid size={5}>
            <Typography variant="label">{t("color_system_code")}</Typography>
          </Grid>
          <Grid size={7}>
            <TextDisplay value={material.color.colorSystemCode} />
          </Grid>
        </>
      )}
      <Grid size={5}>
        <Typography variant="label">{t("color_effects")}</Typography>
      </Grid>
      <Grid size={7}>
        {material.color.colorEffects.length ? (
          <ColorEffectsDisplay value={material.color.colorEffects} />
        ) : (
          <NoData />
        )}
      </Grid>
      <Grid size={5}>
        <Typography variant="label">
          {t("functional_color_properties")}
        </Typography>
      </Grid>
      <Grid size={7}>
        {material.colorTags?.length ? (
          <ColorTagsDisplay value={material.colorTags} />
        ) : (
          <NoData />
        )}
      </Grid>
      {isInternal && material.color?.remarks ? (
        <Grid size={12}>
          <Alert severity="warning">
            <Typography>{`${t("color_system")} / ${t("color_system_code")}`}</Typography>
            <Typography fontSize="var(--body-b2-14-regular-size, 14px)">
              {material.color.remarks}
            </Typography>
          </Alert>
        </Grid>
      ) : (
        ""
      )}
    </Grid>
  );
};
